import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";

const HandleContainer = styled("div")`
  z-index: 1;
  user-select: none;

  & [data-drag-indicator] {
    opacity: 0;
    transition: opacity 0ms;
  }

  &:hover [data-drag-indicator] {
    opacity: 1;
  }
`;

type Props = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  orientation: "vertical" | "horizontal";
  minSize?: number;
  maxSize?: number;
  minOppositeSize?: number;
  defaultSize?: React.CSSProperties["height"];
  initialSize?: number;
  overrideSize?: React.CSSProperties["height"];
  onSizeChange?: (size: number | undefined) => void;
  Handle?: React.ReactNode;
  first: React.ReactNode;
  second: React.ReactNode;
};

export function Resizable({
  containerRef,
  orientation,
  minSize = 0,
  maxSize,
  minOppositeSize = minSize,
  Handle = "div",
  defaultSize = "50%",
  initialSize,
  overrideSize,
  onSizeChange,
  first,
  second,
}: Props) {
  const isDraggingRef = useRef(false);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedSize, setDraggedSize] = useState<number | undefined>(
    initialSize
  );

  const resetSize = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setDraggedSize(undefined);
    onSizeChange?.(undefined);
  }, [onSizeChange]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (overrideSize !== undefined) return;
      if (e.detail > 1) {
        e.preventDefault();
        resetSize();
        return;
      }

      isDraggingRef.current = true;
      setIsDragging(true);
    },
    [resetSize, overrideSize]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const handleSize =
        handleRef.current?.getBoundingClientRect()[
          orientation === "vertical" ? "height" : "width"
        ] ?? 0;
      const computedStyle = getComputedStyle(container);
      const horizontalPadding =
        parseFloat(computedStyle.paddingTop) +
        parseFloat(computedStyle.paddingBottom);
      const verticalPadding =
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);
      const newSize =
        (orientation === "vertical"
          ? rect.bottom - e.clientY
          : rect.right - e.clientX) - handleSize;
      const containerInnerSize =
        orientation === "vertical"
          ? rect.height - horizontalPadding
          : rect.width - verticalPadding;
      const upper = maxSize
        ? Math.min(maxSize, containerInnerSize - minOppositeSize - handleSize)
        : containerInnerSize - minOppositeSize - handleSize;
      const clamped = Math.max(minSize, Math.min(newSize, upper));
      setDraggedSize(clamped);
      onSizeChange?.(clamped);
    },
    [containerRef, maxSize, minSize, minOppositeSize, onSizeChange, orientation]
  );

  const size = overrideSize ?? draggedSize ?? defaultSize;

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const sizeStyle =
    orientation === "vertical" ? { height: size } : { width: size };

  const dynamicHandleStyle =
    orientation === "vertical"
      ? {
          cursor: "ns-resize",
        }
      : {
          cursor: "ew-resize",
        };

  return (
    <>
      {first}

      <HandleContainer
        ref={handleRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={resetSize}
        style={{ ...dynamicHandleStyle }}
      >
        {Handle}
      </HandleContainer>

      <div
        style={{
          ...sizeStyle,
          overflow: "hidden",
          transition:
            overrideSize !== undefined || draggedSize === undefined
              ? `${orientation === "vertical" ? "height" : "width"} 160ms ease-out`
              : undefined,
        }}
      >
        {second}
      </div>

      {isDragging && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            cursor: orientation === "vertical" ? "ns-resize" : "ew-resize",
          }}
        />
      )}
    </>
  );
}
